/**
 * Image compression utility for optimizing photo uploads
 * Resizes images to max 1200px width and compresses to ~0.7 quality
 * Target: Reduce images to under 500KB
 */

const DEFAULT_IMAGE_COMPRESSION_OPTIONS = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.7,
  minQuality: 0.3,
  targetSizeKB: 500,
};

export const PLAN_IMAGE_COMPRESSION_OPTIONS = {
  maxWidth: 2400,
  maxHeight: 2400,
  quality: 0.92,
  minQuality: 0.85,
  targetSizeKB: 2000,
};

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, options = {}) => {
  // Skip compression for non-image files
  if (!file.type.startsWith('image/')) {
    return file;
  }

  const {
    maxWidth,
    maxHeight,
    quality: initialQuality,
    minQuality,
    targetSizeKB,
  } = {
    ...DEFAULT_IMAGE_COMPRESSION_OPTIONS,
    ...options,
  };

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        // Scale down if either dimension exceeds maximum
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Set canvas size
        canvas.width = width;
        canvas.height = height;
        
        // Draw image with high quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try progressively lower quality until we hit target size
        const attemptCompression = (quality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              const sizeKB = blob.size / 1024;
              
              // If still too large and quality can go lower, try again
              if (sizeKB > targetSizeKB && quality > minQuality) {
                attemptCompression(Math.max(minQuality, quality - 0.1));
                return;
              }
              
              // Create new file from blob
              const compressedFileName = file.name.replace(/\.[^/.]+$/, '.jpg');
              
              const compressedFile = new File(
                [blob], 
                compressedFileName, 
                { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                }
              );
              
              console.log(`Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB (quality: ${quality.toFixed(1)})`);
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        
        // Start with initial compression quality
        attemptCompression(initialQuality);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Compress multiple image files
 * @param {File[]} files - Array of image files to compress
 * @returns {Promise<File[]>} - Array of compressed image files (non-images pass through unchanged)
 */
export const compressImages = async (files, options = {}) => {
  return Promise.all(files.map(file => {
    // Only compress image files, pass through others (like PDFs)
    if (file.type.startsWith('image/')) {
      return compressImage(file, options);
    }
    return Promise.resolve(file);
  }));
};
